import { useMemo } from "react";
import { Link } from "react-router";
import { Code2, Copy, ExternalLink, Globe2, Loader2, Send } from "lucide-react";
import { publishForm, useFormList } from "@/hooks/use-forms";
import { formatDateTime } from "@/lib/i18n";

function getBaseUrl(): string {
  if (typeof window === "undefined") {
    return "https://app.stero.ai";
  }

  return window.location.origin;
}

export default function FormEmbedPage() {
  const { forms, isLoading, error, refetch } = useFormList();
  const baseUrl = getBaseUrl();

  const publishedForms = useMemo(
    () => forms.filter((form) => form.status === "published"),
    [forms],
  );

  const draftForms = useMemo(
    () => forms.filter((form) => form.status !== "published"),
    [forms],
  );

  const copySnippet = async (value: string) => {
    await navigator.clipboard.writeText(value);
  };

  const buildPublicUrl = (formId: string) => `${baseUrl}/f/${formId}`;
  const buildIframeSnippet = (formId: string) => {
    const publicUrl = buildPublicUrl(formId);
    return `<iframe src="${publicUrl}" title="Embedded form" loading="lazy" style="width:100%;min-height:720px;border:0;border-radius:16px;"></iframe>`;
  };

  const buildScriptSnippet = (formId: string) => {
    const iframeSnippet = buildIframeSnippet(formId)
      .replace(/"/g, "&quot;")
      .replace(/\n/g, "");

    return `<div id="stero-form-${formId}"></div>
<script>
  (function () {
    var mount = document.getElementById("stero-form-${formId}");
    if (!mount) return;
    mount.innerHTML = "${iframeSnippet}";
  })();
</script>`;
  };

  const handlePublish = async (formId: string) => {
    await publishForm(formId);
    await refetch();
  };

  return (
    <div className="space-y-6">
      <section className="rounded-[28px] border border-border bg-[radial-gradient(circle_at_top_left,_rgba(34,197,94,0.14),_transparent_42%),linear-gradient(135deg,rgba(15,23,42,0.04),rgba(15,23,42,0))] p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-muted-foreground">Embeddable Forms</p>
            <h1 className="text-2xl font-bold text-foreground">Copy truthful embed snippets for the mounted public form runtime.</h1>
            <p className="max-w-3xl text-sm leading-6 text-muted-foreground">
              This workspace closes the stale embed-page gap. It uses the real public route at <code>/f/:formId</code>, gives staff direct public links,
              and provides copyable iframe or script snippets for external sites without inventing a second form runtime.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/pages/forms"
              className="inline-flex items-center gap-2 rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-medium text-foreground transition hover:bg-accent"
            >
              <Send className="h-4 w-4" />
              Review submissions
            </Link>
            <Link
              to="/forms/builder/new"
              className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition hover:bg-primary/90"
            >
              <Code2 className="h-4 w-4" />
              Build new form
            </Link>
          </div>
        </div>
      </section>

      <section className="grid gap-4 lg:grid-cols-3">
        <article className="rounded-[22px] border border-border bg-card p-5 shadow-sm">
          <Globe2 className="h-5 w-5 text-primary" />
          <h2 className="mt-4 text-base font-semibold text-foreground">Mounted public URL</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            Every published form already has a live public route. This page uses that exact runtime instead of a separate embed service.
          </p>
        </article>
        <article className="rounded-[22px] border border-border bg-card p-5 shadow-sm">
          <Copy className="h-5 w-5 text-primary" />
          <h2 className="mt-4 text-base font-semibold text-foreground">Copy ready</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            Teams can copy a raw iframe or a simple script mount, depending on whether the target site accepts direct HTML or needs a drop-in snippet.
          </p>
        </article>
        <article className="rounded-[22px] border border-border bg-card p-5 shadow-sm">
          <ExternalLink className="h-5 w-5 text-primary" />
          <h2 className="mt-4 text-base font-semibold text-foreground">Publish guardrail</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            Draft forms are not embeddable yet. Publish first, then copy snippets from the same mounted public path.
          </p>
        </article>
      </section>

      {isLoading ? (
        <div className="rounded-[24px] border border-dashed border-border bg-card px-6 py-12 text-center text-sm text-muted-foreground">
          <Loader2 className="mx-auto mb-3 h-6 w-6 animate-spin" />
          Loading forms…
        </div>
      ) : error ? (
        <div className="rounded-[24px] border border-destructive/20 bg-destructive/5 px-6 py-4 text-sm text-destructive">
          {error}
        </div>
      ) : (
        <div className="space-y-6">
          <section className="rounded-[24px] border border-border bg-card p-6 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">Published forms</p>
                <h2 className="mt-1 text-lg font-semibold text-foreground">Ready to embed</h2>
              </div>
              <span className="rounded-full border border-border px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                {publishedForms.length} live
              </span>
            </div>

            {publishedForms.length === 0 ? (
              <div className="mt-4 rounded-2xl border border-dashed border-border px-4 py-8 text-sm text-muted-foreground">
                No published forms yet. Publish a form below or create one in the builder first.
              </div>
            ) : (
              <div className="mt-4 space-y-5">
                {publishedForms.map((form) => {
                  const publicUrl = buildPublicUrl(form.id);
                  const iframeSnippet = buildIframeSnippet(form.id);
                  const scriptSnippet = buildScriptSnippet(form.id);

                  return (
                    <article key={form.id} className="rounded-[24px] border border-border bg-background p-5">
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div>
                          <h3 className="text-base font-semibold text-foreground">{form.name}</h3>
                          <p className="mt-1 text-sm text-muted-foreground">
                            {form.submissions} submissions so far. Updated {formatDateTime(form.updatedAt)}.
                          </p>
                        </div>
                        <a
                          href={publicUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-2.5 text-sm font-medium text-foreground transition hover:bg-accent"
                        >
                          <ExternalLink className="h-4 w-4" />
                          Open public form
                        </a>
                      </div>

                      <div className="mt-5 space-y-4">
                        <SnippetCard
                          title="Public URL"
                          value={publicUrl}
                          onCopy={() => void copySnippet(publicUrl)}
                        />
                        <SnippetCard
                          title="Iframe snippet"
                          value={iframeSnippet}
                          onCopy={() => void copySnippet(iframeSnippet)}
                        />
                        <SnippetCard
                          title="Script snippet"
                          value={scriptSnippet}
                          onCopy={() => void copySnippet(scriptSnippet)}
                        />
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>

          {draftForms.length > 0 ? (
            <section className="rounded-[24px] border border-border bg-card p-6 shadow-sm">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">Draft forms</p>
                  <h2 className="mt-1 text-lg font-semibold text-foreground">Publish before embedding</h2>
                </div>
                <span className="rounded-full border border-border px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                  {draftForms.length} drafts
                </span>
              </div>
              <div className="mt-4 space-y-3">
                {draftForms.map((form) => (
                  <div key={form.id} className="flex flex-col gap-3 rounded-2xl border border-border bg-background px-4 py-4 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                      <p className="text-sm font-medium text-foreground">{form.name}</p>
                      <p className="mt-1 text-xs text-muted-foreground">Draft forms do not expose a public embed path yet.</p>
                    </div>
                    <div className="flex gap-3">
                      <Link
                        to={`/forms/builder/${form.id}`}
                        className="inline-flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-2.5 text-sm font-medium text-foreground transition hover:bg-accent"
                      >
                        Edit form
                      </Link>
                      <button
                        type="button"
                        onClick={() => void handlePublish(form.id)}
                        className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition hover:bg-primary/90"
                      >
                        Publish form
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ) : null}
        </div>
      )}
    </div>
  );
}

function SnippetCard({ title, value, onCopy }: { title: string; value: string; onCopy: () => void }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">{title}</p>
        <button
          type="button"
          onClick={onCopy}
          className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground transition hover:bg-accent"
        >
          <Copy className="h-3.5 w-3.5" />
          Copy
        </button>
      </div>
      <pre className="mt-3 overflow-x-auto rounded-xl bg-background p-3 text-xs leading-6 text-foreground">
        <code>{value}</code>
      </pre>
    </div>
  );
}
